import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  DollarSign,
  Eye,
  ImagePlus,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Pencil,
  Plus,
  Save,
  Shield,
  Sparkles,
  TimerReset,
  Trash2,
  WandSparkles,
  X,
  Maximize,
  Minimize
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";

type Route = "/" | "/portfolio" | "/bookings" | "/admin";

type Service = {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  priceCents: number;
  retainerCents: number;
  active: boolean;
  sortOrder: number;
};

type AvailabilityDay = {
  day: number;
  label: string;
  enabled: boolean;
  start: string;
  end: string;
};

type Availability = {
  timezone: string;
  slotIntervalMinutes: number;
  days: AvailabilityDay[];
  blockedDates: string[];
};

type Booking = {
  id: string;
  serviceId: string;
  serviceName: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  status: string;
  notes: string;
  createdAt: string;
};

type Upsell = {
  id: string;
  title: string;
  description: string;
  priceCents: number;
  badge: string;
  icon: "sparkle" | "image" | "map" | "rush";
};

export type PortfolioItem = {
  id: string;
  title: string;
  category: string;
  src: string;
  sortOrder: number;
  showOnHome: boolean;
};

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const fullMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const upsellCatalog: Upsell[] = [
  {
    id: "additional-edited-image",
    title: "Additional Edited Image",
    description: "Add one extra edited digital image to the final gallery with the same color and finish.",
    priceCents: 1000,
    badge: "Most requested",
    icon: "image"
  },
  {
    id: "second-location",
    title: "Upgrade to 2 Locations",
    description: "Add a second nearby Oklahoma City location for more variety in a senior, portrait, or branding session.",
    priceCents: 5000,
    badge: "More variety",
    icon: "map"
  },
  {
    id: "extra-family-member",
    title: "Extra Family Member",
    description: "Add one additional family member beyond the standard small-family package size.",
    priceCents: 1500,
    badge: "Per person",
    icon: "sparkle"
  },
  {
    id: "additional-retouched-headshot",
    title: "Additional Retouched Headshot",
    description: "Add one professionally retouched headshot to a headshot session delivery.",
    priceCents: 2000,
    badge: "Per image",
    icon: "image"
  },
  {
    id: "extra-event-hour",
    title: "Additional Event Hour",
    description: "Extend event coverage by one hour after the two-hour minimum.",
    priceCents: 10000,
    badge: "Per hour",
    icon: "rush"
  },
  {
    id: "priority-preview",
    title: "Priority Preview Gallery",
    description: "Request a small preview set first for announcements, business updates, or social launch timing.",
    priceCents: 5000,
    badge: "Optional rush",
    icon: "rush"
  }
];

const upsellRecommendations: Record<string, string[]> = {
  "mini-session": ["additional-edited-image", "priority-preview"],
  "standard-portrait": ["additional-edited-image", "second-location", "priority-preview"],
  "family-session": ["additional-edited-image", "extra-family-member", "priority-preview"],
  "senior-session": ["second-location", "additional-edited-image", "priority-preview"],
  "headshot-session": ["additional-retouched-headshot", "priority-preview"],
  "branding-session": ["second-location", "additional-edited-image", "priority-preview"],
  "event-photography": ["extra-event-hour", "priority-preview"]
};

const routeMeta: Record<Route, { title: string; description: string }> = {
  "/": {
    title: "Oklahoma City Photographer | Bambo Photography",
    description:
      "Bambo Photography is an Oklahoma City photographer specializing in portraits, senior photos, family sessions, headshots, branding, and event photography."
  },
  "/portfolio": {
    title: "Oklahoma City Photography Portfolio | Bambo Photography",
    description:
      "Explore portrait, senior, family, headshot, branding, and event photography from Bambo Photography in Oklahoma City."
  },
  "/bookings": {
    title: "Book an Oklahoma City Photography Session | Bambo Photography",
    description:
      "Reserve an Oklahoma City photography session for portraits, family photos, senior photos, headshots, branding, or event coverage."
  },
  "/admin": {
    title: "Studio Admin | Bambo Photography",
    description: "Secure studio controls for Bambo Photography bookings, services, and availability."
  }
};

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  try {
    const response = await fetch(path, {
      ...init,
      headers,
      credentials: "include",
      cache: method === "GET" ? "no-store" : init.cache
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error || response.statusText || "Request failed.");
    }

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      throw new Error("API did not return JSON.");
    }

    return response.json() as Promise<T>;
  } catch (error) {
    if (import.meta.env.DEV) {
      const { mockApi } = await import("./mockApi");
      return mockApi<T>(path, init);
    }
    throw error;
  }
}

function currency(cents: number) {
  if (!cents) {
    return "Custom";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(cents / 100);
}

function durationLabel(minutes: number) {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} hr ${remaining} min` : `${hours} hr`;
}

function getUpsellsForService(service?: Service) {
  if (!service) {
    return upsellCatalog.slice(0, 4);
  }

  const recommendedIds =
    upsellRecommendations[service.id] ??
    (service.name.toLowerCase().includes("brand")
      ? upsellRecommendations["branding-session"]
      : upsellRecommendations["standard-portrait"]);

  return recommendedIds
    .map((id) => upsellCatalog.find((upsell) => upsell.id === id))
    .filter((upsell): upsell is Upsell => Boolean(upsell));
}

function prettyDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(new Date(year, month - 1, day));
}

function toDateString(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function heroImageSrcSet(src: string) {
  try {
    const url = new URL(src, window.location.origin);
    if (!url.hostname.endsWith("images.unsplash.com")) {
      return undefined;
    }

    return [640, 960, 1400, 1800, 2200]
      .map((width) => {
        const variant = new URL(url.toString());
        variant.searchParams.set("auto", "format");
        variant.searchParams.set("fit", "crop");
        variant.searchParams.set("w", String(width));
        variant.searchParams.set("q", width <= 960 ? "82" : "88");
        return `${variant.toString()} ${width}w`;
      })
      .join(", ");
  } catch {
    return undefined;
  }
}

function dateFromString(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
}

function timeToMinutes(value: string) {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function hasBookableWindow(day: AvailabilityDay, service?: Service) {
  if (!day.enabled) {
    return false;
  }

  if (!service) {
    return true;
  }

  return timeToMinutes(day.end) - timeToMinutes(day.start) >= service.durationMinutes;
}

function isBookableCalendarDate(date: Date, value: string, availability: Availability, service?: Service) {
  if (availability.blockedDates.includes(value)) {
    return false;
  }

  const day = availability.days.find((item) => item.day === date.getDay());
  return Boolean(day && hasBookableWindow(day, service));
}

function useRoute() {
  const [route, setRoute] = useState<Route>(() => normalizeRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(normalizeRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (nextRoute: Route) => {
    window.history.pushState({}, "", nextRoute);
    setRoute(nextRoute);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { route, navigate };
}

function normalizeRoute(pathname: string): Route {
  if (pathname.startsWith("/portfolio")) return "/portfolio";
  if (pathname.startsWith("/bookings")) return "/bookings";
  if (pathname.startsWith("/admin")) return "/admin";
  return "/";
}

function useReveal(route: Route) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );

    const observed = new WeakSet<Element>();
    const observeRevealElements = () => {
      document.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-visible)").forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.96 && rect.bottom > 0) {
          element.classList.add("is-visible");
          return;
        }

        if (!observed.has(element)) {
          observed.add(element);
          observer.observe(element);
        }
      });
    };

    observeRevealElements();
    const mutationObserver = new MutationObserver(observeRevealElements);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [route]);
}

function usePageMeta(route: Route) {
  useEffect(() => {
    const meta = routeMeta[route];
    document.title = meta.title;

    const setMeta = (selector: string, attribute: "content" | "href", value: string) => {
      const element = document.head.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      }
    };

    const canonicalUrl = `${window.location.origin}${route === "/" ? "/" : route}`;
    const socialImageUrl = `${window.location.origin}/social-preview.svg`;
    setMeta('meta[name="description"]', "content", meta.description);
    setMeta('meta[name="robots"]', "content", route === "/admin" ? "noindex, nofollow" : "index, follow");
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:image"]', "content", socialImageUrl);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
    setMeta('meta[name="twitter:image"]', "content", socialImageUrl);
    setMeta('link[rel="canonical"]', "href", canonicalUrl);
  }, [route]);
}

export function App() {
  const { route, navigate } = useRoute();
  const [navOpen, setNavOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState<{src: string; alt: string} | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [heroImage, setHeroImage] = useState("https://images.unsplash.com/photo-1523438885200-e635ba2c371e?auto=format&fit=crop&w=2200&q=88");

  useEffect(() => {
    api<{portfolio: PortfolioItem[], heroImage?: string}>("/api/portfolio")
      .then(data => {
        setPortfolio(data.portfolio);
        if (data.heroImage) setHeroImage(data.heroImage);
      })
      .catch(() => null);
  }, []);
  useReveal(route);
  usePageMeta(route);

  return (
    <>
      <a className="skip-link" href="#content">
        Skip to content
      </a>
      <SiteHeader route={route} navigate={navigate} navOpen={navOpen} setNavOpen={setNavOpen} />
      <main id="content">
        {route === "/" && <HomePage navigate={navigate} onImageClick={setLightboxImg} portfolio={portfolio} heroImage={heroImage} />}
        {route === "/portfolio" && <PortfolioPage navigate={navigate} onImageClick={setLightboxImg} portfolio={portfolio} />}
        {route === "/bookings" && <BookingsPage />}
        {route === "/admin" && <AdminPage />}
      </main>
      <Footer />
      {lightboxImg && (
        <Lightbox 
          src={lightboxImg.src} 
          alt={lightboxImg.alt} 
          onClose={() => setLightboxImg(null)} 
        />
      )}
    </>
  );
}

function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const [fitToScreen, setFitToScreen] = useState(true);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    
    // Prevent background scrolling
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  return (
    <div 
      className="modal-backdrop lightbox-backdrop" 
      onClick={onClose} 
      style={{ 
        zIndex: 100, 
        cursor: fitToScreen ? "zoom-in" : "zoom-out", 
        padding: "40px",
        display: "flex",
        alignItems: fitToScreen ? "center" : "flex-start",
        justifyContent: fitToScreen ? "center" : "flex-start",
        overflow: "auto"
      }}
    >
      <img 
        src={src} 
        alt={alt} 
        style={{ 
          maxHeight: fitToScreen ? "100%" : "none", 
          maxWidth: fitToScreen ? "100%" : "none", 
          objectFit: "contain", 
          borderRadius: "8px", 
          boxShadow: "0 26px 90px rgba(0, 0, 0, 0.48)",
          margin: fitToScreen ? "0" : "auto",
          transition: "max-width 0.3s ease, max-height 0.3s ease"
        }} 
        onClick={(e) => {
          e.stopPropagation();
          setFitToScreen(!fitToScreen);
        }} 
      />
      
      <div style={{ position: "fixed", top: "20px", right: "20px", display: "flex", gap: "12px", zIndex: 101 }} onClick={(e) => e.stopPropagation()}>
        <button 
          className="icon-button" 
          style={{ border: "none", color: "white", background: "rgba(0,0,0,0.6)" }} 
          onClick={() => setFitToScreen(!fitToScreen)}
          title={fitToScreen ? "Actual Size" : "Fit to Screen"}
          aria-label={fitToScreen ? "Actual Size" : "Fit to Screen"}
        >
          {fitToScreen ? <Maximize size={20} aria-hidden="true" /> : <Minimize size={20} aria-hidden="true" />}
        </button>
        <button 
          className="icon-button" 
          style={{ border: "none", color: "white", background: "rgba(0,0,0,0.6)" }} 
          onClick={onClose}
          aria-label="Close image"
          title="Close"
        >
          <X size={24} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function SiteHeader({
  route,
  navigate,
  navOpen,
  setNavOpen
}: {
  route: Route;
  navigate: (route: Route) => void;
  navOpen: boolean;
  setNavOpen: (value: boolean) => void;
}) {
  const navItems: Array<{ label: string; route: Route }> = [
    { label: "Portfolio", route: "/portfolio" }
  ];

  return (
    <header className="site-header">
      <button
        className="brand"
        onClick={() => navigate("/")}
        aria-label="Bambo Photography home"
        type="button"
      >
        <span className="brand-mark">
          <img src="/logo-mark.svg" alt="" aria-hidden="true" />
        </span>
        <span>Bambo Photography</span>
      </button>

      <nav className={navOpen ? "nav is-open" : "nav"} aria-label="Main navigation">
        {navItems.map((item) => (
          <button
            key={item.route}
            className={route === item.route ? "nav-link is-active" : "nav-link"}
            onClick={() => {
              navigate(item.route);
              setNavOpen(false);
            }}
            type="button"
            aria-current={route === item.route ? "page" : undefined}
          >
            {item.label}
          </button>
        ))}
        <button
          className="button button-dark nav-cta"
          onClick={() => {
            navigate("/bookings");
            setNavOpen(false);
          }}
          type="button"
          aria-current={route === "/bookings" ? "page" : undefined}
        >
          <CalendarDays size={18} aria-hidden="true" />
          Book
        </button>
      </nav>

      <button
        className="icon-button mobile-toggle"
        onClick={() => setNavOpen(!navOpen)}
        aria-label={navOpen ? "Close navigation" : "Open navigation"}
        type="button"
      >
        {navOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
      </button>
    </header>
  );
}

function HomePage({ navigate, onImageClick, portfolio, heroImage }: { navigate: (route: Route) => void; onImageClick: (img: {src: string; alt: string}) => void; portfolio: PortfolioItem[], heroImage: string }) {
  return (
    <>
      <section className="hero" itemScope itemType="https://schema.org/ProfessionalService">
        <meta itemProp="areaServed" content="Oklahoma City, Oklahoma" />
        <meta
          itemProp="serviceType"
          content="Portrait photography, family photography, senior photography, headshot photography, branding photography, event photography"
        />
        <img
          className="hero-image"
          src={heroImage}
          srcSet={heroImageSrcSet(heroImage)}
          sizes="100vw"
          alt="Professional photography session with a couple in warm cinematic light"
          loading="eager"
          decoding="async"
          itemProp="image"
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <p className="eyebrow">Oklahoma City Photographer</p>
          <h1 itemProp="name">Bambo</h1>
          <p className="hero-copy" itemProp="description">
            Quiet moments. Editorial finish.
          </p>
          <div className="hero-actions">
            <button className="button button-light" type="button" onClick={() => navigate("/portfolio")}>
              <Eye size={18} aria-hidden="true" />
              View Work
            </button>
            <button className="button button-ghost-light" type="button" onClick={() => navigate("/bookings")}>
              <CalendarDays size={18} aria-hidden="true" />
              Book Session
            </button>
          </div>
        </div>
      </section>

      <section className="statement section-pad">
        <div className="statement-grid">
          <h2 data-reveal>Calm direction. Lasting imagery.</h2>
          <p data-reveal>
            Guided, stress-free sessions in Oklahoma City, producing timeless galleries with a clean, editorial edge.
          </p>
        </div>
      </section>

      <section className="portfolio-strip" aria-label="Featured portfolio">
        {portfolio.filter(item => item.showOnHome).map((item, index) => (
          <article 
            className="strip-item" 
            data-reveal 
            key={item.title} 
            style={{ transitionDelay: `${index * 70}ms`, cursor: 'zoom-in' }}
            onClick={() => onImageClick({ src: item.src.replace('w=1400', 'w=2800'), alt: item.title })}
          >
            <img src={item.src} alt={item.title} loading="lazy" />
            <div>
              <span>{item.category}</span>
              <h3>{item.title}</h3>
            </div>
          </article>
        ))}
      </section>

      <section className="section-pad service-preview">
        <div className="section-heading" data-reveal>
          <p className="eyebrow">Services</p>
          <h2>Captured with intention.</h2>
        </div>
        <div className="service-preview-grid">
          {[
            ["Portraits", "Family, senior, and individual sessions with clear direction."],
            ["Brands", "Polished imagery for professionals and small businesses."],
            ["Events", "Unobtrusive coverage for your most important gatherings."]
          ].map(([title, copy], index) => (
            <article className="feature-card" data-reveal key={title} style={{ transitionDelay: `${index * 80}ms` }}>
              <span>0{index + 1}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="booking-band">
        <div data-reveal>
          <p className="eyebrow">Bookings</p>
          <h2>Reserve your date.</h2>
        </div>
        <button className="button button-light" type="button" onClick={() => navigate("/bookings")}>
          <CalendarDays size={18} aria-hidden="true" />
          Book Now
        </button>
      </section>
    </>
  );
}

function PortfolioPage({ navigate, onImageClick, portfolio }: { navigate: (route: Route) => void; onImageClick: (img: {src: string; alt: string}) => void; portfolio: PortfolioItem[] }) {
  return (
    <section className="page-shell">
      <div className="page-kicker" data-reveal>
        <p className="eyebrow">Portfolio</p>
        <h1>Quiet images. Lasting gravity.</h1>
      </div>
      <div className="masonry-gallery">
        {portfolio.map((item, index) => (
          <article 
            className="gallery-item" 
            data-reveal 
            key={item.title} 
            style={{ transitionDelay: `${index * 60}ms`, cursor: 'zoom-in' }}
            onClick={() => onImageClick({ src: item.src.replace('w=1400', 'w=2800'), alt: item.title })}
          >
            <img src={item.src} alt={item.title} loading={index < 2 ? "eager" : "lazy"} />
            <div>
              <span>{item.category}</span>
              <h2>{item.title}</h2>
            </div>
          </article>
        ))}
      </div>
      <div className="center-action">
        <button className="button button-dark" type="button" onClick={() => navigate("/bookings")}>
          <CalendarDays size={18} aria-hidden="true" />
          Check Dates
        </button>
      </div>
    </section>
  );
}

function BookingsPage() {
  const [services, setServices] = useState<Service[]>([]);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ clientName: "", clientEmail: "", clientPhone: "", notes: "" });
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [selectedUpsellIds, setSelectedUpsellIds] = useState<string[]>([]);

  const selectedService = services.find((service) => service.id === selectedServiceId);
  const recommendedUpsells = useMemo(() => getUpsellsForService(selectedService), [selectedService]);
  const selectedUpsells = recommendedUpsells.filter((upsell) => selectedUpsellIds.includes(upsell.id));
  const upsellTotalCents = selectedUpsells.reduce((total, upsell) => total + upsell.priceCents, 0);
  const estimatedTotalCents = (selectedService?.priceCents ?? 0) + upsellTotalCents;

  useEffect(() => {
    Promise.all([api<{ services: Service[] }>("/api/services"), api<{ availability: Availability }>("/api/availability")])
      .then(([servicesData, availabilityData]) => {
        setServices(servicesData.services);
        setSelectedServiceId(servicesData.services[0]?.id ?? "");
        setAvailability(availabilityData.availability);
      })
      .catch((reason) => setError(reason.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!availability || !selectedDate) {
      return;
    }

    const date = dateFromString(selectedDate);
    if (!date || !isBookableCalendarDate(date, selectedDate, availability, selectedService)) {
      setSelectedDate("");
      setSelectedTime("");
      setSlots([]);
    }
  }, [availability, selectedDate, selectedService]);

  useEffect(() => {
    if (!selectedDate || !selectedServiceId) {
      setSlots([]);
      return;
    }

    setSlotLoading(true);
    setSelectedTime("");
    api<{ slots: string[] }>(`/api/slots?serviceId=${encodeURIComponent(selectedServiceId)}&date=${selectedDate}`)
      .then((data) => setSlots(data.slots))
      .catch((reason) => setError(reason.message))
      .finally(() => setSlotLoading(false));
  }, [selectedDate, selectedServiceId]);

  useEffect(() => {
    setSelectedUpsellIds((current) =>
      current.filter((id) => getUpsellsForService(selectedService).some((upsell) => upsell.id === id))
    );
  }, [selectedService]);

  const toggleUpsell = (id: string) => {
    setSelectedUpsellIds((current) =>
      current.includes(id) ? current.filter((upsellId) => upsellId !== id) : [...current, id]
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedServiceId || !selectedDate || !selectedTime) {
      setError("Choose a service, date, and time.");
      return;
    }

    try {
      const addonNotes = selectedUpsells.length
        ? `Requested add-ons: ${selectedUpsells
            .map((upsell) => `${upsell.title} (${currency(upsell.priceCents)})`)
            .join(", ")}.`
        : "";
      const combinedNotes = [form.notes.trim(), addonNotes].filter(Boolean).join("\n\n");

      await api("/api/bookings", {
        method: "POST",
        body: JSON.stringify({
          serviceId: selectedServiceId,
          date: selectedDate,
          startTime: selectedTime,
          ...form,
          notes: combinedNotes
        })
      });
      setSuccess("Request received. Bambo will confirm the session shortly.");
      setForm({ clientName: "", clientEmail: "", clientPhone: "", notes: "" });
      setSelectedUpsellIds([]);
      const refreshed = await api<{ slots: string[] }>(
        `/api/slots?serviceId=${encodeURIComponent(selectedServiceId)}&date=${selectedDate}`
      );
      setSlots(refreshed.slots);
      setSelectedTime("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Booking failed.");
    }
  };

  return (
    <section className="page-shell booking-page">
      <div className="page-kicker" data-reveal>
        <p className="eyebrow">Bookings</p>
        <h1>Reserve a session.</h1>
      </div>

      {loading ? (
        <div className="loading-panel">Loading availability...</div>
      ) : (
        <>
          <form className="booking-layout" onSubmit={submit}>
            <div className="booking-column" data-reveal>
              <SectionLabel icon={<Sparkles size={18} aria-hidden="true" />} label="Service" />
              <div className="service-options">
                {services.map((service) => (
                  <button
                    type="button"
                    className={selectedServiceId === service.id ? "service-option is-selected" : "service-option"}
                    key={service.id}
                    onClick={() => setSelectedServiceId(service.id)}
                  >
                    <span className="service-option-top">
                      <strong>{service.name}</strong>
                      <small>{durationLabel(service.durationMinutes)}</small>
                    </span>
                    <span className="service-price-line">
                      <strong>{currency(service.priceCents)}</strong>
                      {service.retainerCents > 0 && <small>Retainer {currency(service.retainerCents)}</small>}
                    </span>
                    <p>{service.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="booking-column" data-reveal>
              <SectionLabel icon={<CalendarDays size={18} aria-hidden="true" />} label="Date" />
              <CalendarPicker
                month={month}
                setMonth={setMonth}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                availability={availability}
                selectedService={selectedService}
              />
            </div>

            <div className="booking-column" data-reveal>
              <SectionLabel icon={<Clock3 size={18} aria-hidden="true" />} label="Time" />
              <div className="time-grid">
                {slotLoading && <span className="muted">Checking times...</span>}
                {!slotLoading &&
                  slots.map((slot) => (
                    <button
                      className={selectedTime === slot ? "time-chip is-selected" : "time-chip"}
                      key={slot}
                      type="button"
                      onClick={() => setSelectedTime(slot)}
                    >
                      {slot}
                    </button>
                  ))}
                {!slotLoading && selectedDate && !slots.length && (
                  <span className="muted">No openings on {prettyDate(selectedDate)}.</span>
                )}
                {!selectedDate && <span className="muted">Select a date.</span>}
              </div>

              <div className="upsell-callout">
                <div>
                  <span>Session enhancements</span>
                  <strong>{selectedUpsells.length ? `${selectedUpsells.length} selected` : "Add-ons available"}</strong>
                </div>
                <button className="button button-outline" type="button" onClick={() => setUpsellOpen(true)}>
                  <WandSparkles size={18} aria-hidden="true" />
                  Customize
                </button>
              </div>

              <div className="booking-form">
                <label>
                  Name
                  <input
                    required
                    value={form.clientName}
                    onChange={(event) => setForm({ ...form, clientName: event.target.value })}
                    autoComplete="name"
                  />
                </label>
                <label>
                  Email
                  <input
                    required
                    type="email"
                    value={form.clientEmail}
                    onChange={(event) => setForm({ ...form, clientEmail: event.target.value })}
                    autoComplete="email"
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={form.clientPhone}
                    onChange={(event) => setForm({ ...form, clientPhone: event.target.value })}
                    autoComplete="tel"
                  />
                </label>
                <label>
                  Notes
                  <textarea
                    value={form.notes}
                    onChange={(event) => setForm({ ...form, notes: event.target.value })}
                    rows={4}
                  />
                </label>
              </div>

              <div className="booking-summary">
                <div>
                  <span>Collection</span>
                  <strong>{selectedService?.name ?? "Select service"}</strong>
                </div>
                <div>
                  <span>Date</span>
                  <strong>{selectedDate ? prettyDate(selectedDate) : "Select date"}</strong>
                </div>
                <div>
                  <span>Time</span>
                  <strong>{selectedTime || "Select time"}</strong>
                </div>
                {selectedUpsells.length > 0 && (
                  <div>
                    <span>Add-ons</span>
                    <strong>{selectedUpsells.map((upsell) => upsell.title).join(", ")}</strong>
                  </div>
                )}
                <div className="summary-total">
                  <span>Estimated total</span>
                  <strong>{selectedService?.priceCents ? currency(estimatedTotalCents) : "Custom quote"}</strong>
                </div>
              </div>

              {error && <p className="form-message is-error">{error}</p>}
              {success && <p className="form-message is-success">{success}</p>}

              <button className="button button-dark submit-button" type="submit">
                <Check size={18} aria-hidden="true" />
                Request Booking
              </button>
            </div>
          </form>
          <UpsellModal
            open={upsellOpen}
            onClose={() => setUpsellOpen(false)}
            upsells={recommendedUpsells}
            selectedIds={selectedUpsellIds}
            onToggle={toggleUpsell}
            totalCents={upsellTotalCents}
          />
        </>
      )}
    </section>
  );
}

function upsellIcon(icon: Upsell["icon"]) {
  if (icon === "image") return <ImagePlus size={20} aria-hidden="true" />;
  if (icon === "map") return <MapPin size={20} aria-hidden="true" />;
  if (icon === "rush") return <TimerReset size={20} aria-hidden="true" />;
  return <Sparkles size={20} aria-hidden="true" />;
}

function UpsellModal({
  open,
  onClose,
  upsells,
  selectedIds,
  onToggle,
  totalCents
}: {
  open: boolean;
  onClose: () => void;
  upsells: Upsell[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  totalCents: number;
}) {
  useEffect(() => {
    if (!open) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className="upsell-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upsell-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-top">
          <div>
            <p className="eyebrow">Session enhancements</p>
            <h2 id="upsell-title">Refine the booking.</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close add-ons">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="upsell-grid">
          {upsells.map((upsell) => {
            const selected = selectedIds.includes(upsell.id);
            return (
              <button
                className={selected ? "upsell-card is-selected" : "upsell-card"}
                key={upsell.id}
                type="button"
                onClick={() => onToggle(upsell.id)}
              >
                <span className="upsell-icon">{upsellIcon(upsell.icon)}</span>
                <span className="upsell-badge">{upsell.badge}</span>
                <strong>{upsell.title}</strong>
                <small>{currency(upsell.priceCents)}</small>
                <p>{upsell.description}</p>
                <span className="upsell-state">{selected ? "Selected" : "Add to request"}</span>
              </button>
            );
          })}
        </div>

        <div className="modal-bottom">
          <div>
            <span>Add-on estimate</span>
            <strong>{totalCents ? currency(totalCents) : "$0"}</strong>
          </div>
          <button className="button button-light" type="button" onClick={onClose}>
            <Check size={18} aria-hidden="true" />
            Apply
          </button>
        </div>
      </section>
    </div>
  );
}

function CalendarPicker({
  month,
  setMonth,
  selectedDate,
  setSelectedDate,
  availability,
  selectedService
}: {
  month: Date;
  setMonth: (date: Date) => void;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  availability: Availability | null;
  selectedService?: Service;
}) {
  const today = toDateString(new Date());
  const cells = useMemo(() => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const first = new Date(year, monthIndex, 1);
    const count = new Date(year, monthIndex + 1, 0).getDate();
    const blanks = Array.from({ length: first.getDay() }, () => null);
    const dates = Array.from({ length: count }, (_, index) => new Date(year, monthIndex, index + 1));
    return [...blanks, ...dates];
  }, [month]);

  return (
    <div className="calendar">
      <div className="calendar-top">
        <button
          type="button"
          className="icon-button"
          aria-label="Previous month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <strong>
          {fullMonths[month.getMonth()]} {month.getFullYear()}
        </strong>
        <button
          type="button"
          className="icon-button"
          aria-label="Next month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="weekday-grid">
        {weekdays.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="date-grid">
        {cells.map((date, index) => {
          if (!date) {
            return <span className="date-cell is-empty" key={`empty-${index}`} />;
          }
          const value = toDateString(date);
          const isPast = value < today;
          const available = availability ? isBookableCalendarDate(date, value, availability, selectedService) : true;
          const disabled = isPast || !available;
          const className = [
            "date-cell",
            selectedDate === value ? "is-selected" : "",
            value === today ? "is-today" : "",
            !isPast && !available ? "is-unavailable" : ""
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              aria-label={`${prettyDate(value)}${disabled ? ", unavailable" : ", available"}`}
              className={className}
              disabled={disabled}
              key={value}
              type="button"
              title={disabled ? "Unavailable based on the current admin schedule" : "Available"}
              onClick={() => setSelectedDate(value)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <p className="calendar-note">Available dates update from the admin schedule.</p>
    </div>
  );
}

function SectionLabel({ icon, label }: { icon: JSX.Element; label: string }) {
  return (
    <div className="section-label">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function AdminPage() {
  const [checked, setChecked] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState<"menu" | "availability" | "bookings" | "portfolio">("menu");

  useEffect(() => {
    api<{ authenticated: boolean }>("/api/admin/me")
      .then((data) => setAuthed(data.authenticated))
      .catch(() => setAuthed(false))
      .finally(() => setChecked(true));
  }, []);

  const logout = async () => {
    await api("/api/admin/logout", { method: "POST" }).catch(() => null);
    setAuthed(false);
  };

  if (!checked) {
    return (
      <section className="page-shell">
        <div className="loading-panel">Checking admin session...</div>
      </section>
    );
  }

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }

  return (
    <section className="page-shell admin-page">
      <div className="admin-top">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Studio controls.</h1>
        </div>
        <button className="button button-outline" type="button" onClick={logout}>
          <LogOut size={18} aria-hidden="true" />
          Sign Out
        </button>
      </div>

      <div className="admin-tabs" role="tablist" aria-label="Admin sections">
        {[
          ["menu", "Menu"],
          ["availability", "Availability"],
          ["bookings", "Bookings"],
          ["portfolio", "Portfolio"]
        ].map(([value, label]) => (
          <button
            className={tab === value ? "tab-button is-selected" : "tab-button"}
            key={value}
            type="button"
            onClick={() => setTab(value as typeof tab)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "menu" && <MenuAdmin />}
      {tab === "availability" && <AvailabilityAdmin />}
      {tab === "bookings" && <BookingsAdmin />}
      {tab === "portfolio" && <PortfolioAdmin />}
    </section>
  );
}

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState("studio");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ username, password })
      });
      onLogin();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Login failed.");
    }
  };

  return (
    <section className="page-shell login-shell">
      <form className="login-panel" onSubmit={submit}>
        <Shield size={28} aria-hidden="true" />
        <h1>Studio login.</h1>
        <label>
          Username
          <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
        </label>
        <label>
          Password
          <input
            required
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </label>
        {error && <p className="form-message is-error">{error}</p>}
        <button className="button button-dark submit-button" type="submit">
          <Shield size={18} aria-hidden="true" />
          Enter Admin
        </button>
      </form>
    </section>
  );
}

function MenuAdmin() {
  const [services, setServices] = useState<Service[]>([]);
  const [message, setMessage] = useState("");

  const load = () => {
    api<{ services: Service[] }>("/api/services?all=1").then((data) => setServices(data.services));
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async (service: Service) => {
    await api(`/api/services/${service.id}`, {
      method: "PUT",
      body: JSON.stringify(service)
    });
    setMessage("Menu saved.");
    load();
  };

  const add = async () => {
    await api("/api/services", {
      method: "POST",
      body: JSON.stringify({
        name: "New Collection",
        description: "Describe this collection.",
        durationMinutes: 60,
        priceCents: 50000,
        retainerCents: 15000,
        active: true,
        sortOrder: services.length * 10 + 50
      })
    });
    load();
  };

  const update = (id: string, patch: Partial<Service>) => {
    setServices((current) => current.map((service) => (service.id === id ? { ...service, ...patch } : service)));
  };

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <SectionLabel icon={<DollarSign size={18} aria-hidden="true" />} label="Booking Menu" />
        <button className="button button-dark" type="button" onClick={add}>
          <Plus size={18} aria-hidden="true" />
          Add Service
        </button>
      </div>
      <div className="service-admin-list">
        {services.map((service) => (
          <article className="admin-row" key={service.id}>
            <label>
              Name
              <input value={service.name} onChange={(event) => update(service.id, { name: event.target.value })} />
            </label>
            <label>
              Description
              <textarea
                value={service.description}
                onChange={(event) => update(service.id, { description: event.target.value })}
                rows={3}
              />
            </label>
            <div className="field-grid">
              <label>
                Minutes
                <input
                  type="number"
                  value={service.durationMinutes}
                  onChange={(event) => update(service.id, { durationMinutes: Number(event.target.value) })}
                />
              </label>
              <label>
                Price
                <input
                  type="number"
                  value={service.priceCents / 100}
                  onChange={(event) => update(service.id, { priceCents: Number(event.target.value) * 100 })}
                />
              </label>
              <label>
                Retainer
                <input
                  type="number"
                  value={service.retainerCents / 100}
                  onChange={(event) => update(service.id, { retainerCents: Number(event.target.value) * 100 })}
                />
              </label>
              <label>
                Sort
                <input
                  type="number"
                  value={service.sortOrder}
                  onChange={(event) => update(service.id, { sortOrder: Number(event.target.value) })}
                />
              </label>
            </div>
            <div className="row-actions">
              <label className="toggle-line">
                <input
                  type="checkbox"
                  checked={service.active}
                  onChange={(event) => update(service.id, { active: event.target.checked })}
                />
                Active
              </label>
              <button className="icon-action" type="button" onClick={() => update(service.id, { active: false })}>
                <Trash2 size={18} aria-hidden="true" />
                Archive
              </button>
              <button className="button button-dark" type="button" onClick={() => save(service)}>
                <Save size={18} aria-hidden="true" />
                Save
              </button>
            </div>
          </article>
        ))}
      </div>
      {message && <p className="form-message is-success">{message}</p>}
    </div>
  );
}

function AvailabilityAdmin() {
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api<{ availability: Availability }>("/api/availability").then((data) => setAvailability(data.availability));
  }, []);

  const updateDay = (day: number, patch: Partial<AvailabilityDay>) => {
    setAvailability((current) =>
      current
        ? {
            ...current,
            days: current.days.map((item) => (item.day === day ? { ...item, ...patch } : item))
          }
        : current
    );
  };

  const save = async () => {
    if (!availability) return;
    const data = await api<{ availability: Availability }>("/api/availability", {
      method: "PUT",
      body: JSON.stringify(availability)
    });
    setAvailability(data.availability);
    setMessage("Availability saved.");
  };

  if (!availability) {
    return <div className="loading-panel">Loading availability...</div>;
  }

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <SectionLabel icon={<Clock3 size={18} aria-hidden="true" />} label="Weekly Availability" />
        <button className="button button-dark" type="button" onClick={save}>
          <Save size={18} aria-hidden="true" />
          Save
        </button>
      </div>
      <div className="availability-tools">
        <label>
          Timezone
          <input
            value={availability.timezone}
            onChange={(event) => setAvailability({ ...availability, timezone: event.target.value })}
          />
        </label>
        <label>
          Slot Interval
          <input
            type="number"
            min={15}
            step={15}
            value={availability.slotIntervalMinutes}
            onChange={(event) =>
              setAvailability({ ...availability, slotIntervalMinutes: Number(event.target.value) })
            }
          />
        </label>
      </div>
      <div className="availability-list">
        {availability.days.map((day) => (
          <article className="availability-row" key={day.day}>
            <label className="toggle-line">
              <input
                type="checkbox"
                checked={day.enabled}
                onChange={(event) => updateDay(day.day, { enabled: event.target.checked })}
              />
              {day.label}
            </label>
            <label>
              Start
              <input type="time" value={day.start} onChange={(event) => updateDay(day.day, { start: event.target.value })} />
            </label>
            <label>
              End
              <input type="time" value={day.end} onChange={(event) => updateDay(day.day, { end: event.target.value })} />
            </label>
          </article>
        ))}
      </div>
      {message && <p className="form-message is-success">{message}</p>}
    </div>
  );
}

function BookingsAdmin() {
  const [bookings, setBookings] = useState<Booking[]>([]);

  const load = () => api<{ bookings: Booking[] }>("/api/bookings").then((data) => setBookings(data.bookings));

  useEffect(() => {
    void load();
  }, []);

  const setStatus = async (booking: Booking, status: string) => {
    await api(`/api/bookings/${booking.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    });
    load();
  };

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <SectionLabel icon={<Mail size={18} aria-hidden="true" />} label="Booking Requests" />
      </div>
      <div className="booking-admin-list">
        {bookings.map((booking) => (
          <article className="booking-admin-row" key={booking.id}>
            <div>
              <span className={`status-pill status-${booking.status}`}>{booking.status}</span>
              <h2>{booking.clientName}</h2>
              <p>
                {booking.serviceName} on {prettyDate(booking.date)} at {booking.startTime}
              </p>
              <p>
                {booking.clientEmail}
                {booking.clientPhone ? ` / ${booking.clientPhone}` : ""}
              </p>
              {booking.notes && <p>{booking.notes}</p>}
            </div>
            <select value={booking.status} onChange={(event) => setStatus(booking, event.target.value)}>
              <option value="pending">pending</option>
              <option value="confirmed">confirmed</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </article>
        ))}
        {!bookings.length && <div className="loading-panel">No booking requests yet.</div>}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <span className="brand-line">Bambo Photography</span>
      </div>
    </footer>
  );
}

function PortfolioAdmin() {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", category: "", sortOrder: 0, showOnHome: true });
  const [files, setFiles] = useState<File[]>([]);
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [heroSaving, setHeroSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<PortfolioItem>>({});

  const load = () => {
    setLoading(true);
    api<{ portfolio: PortfolioItem[] }>("/api/portfolio")
      .then((data) => setItems(data.portfolio))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!files.length) {
      setError("Please select at least one image file.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const uploads = files.map((file, index) => {
        const form = new FormData();
        const baseTitle = newItem.title || file.name.replace(/\.[^/.]+$/, "");
        const title = files.length > 1 && newItem.title ? `${baseTitle} ${index + 1}` : baseTitle;
        
        form.append("title", title);
        form.append("category", newItem.category || "Uncategorized");
        form.append("sortOrder", (newItem.sortOrder + index * 10).toString());
        form.append("showOnHome", newItem.showOnHome ? "true" : "false");
        form.append("image", file);

        return api("/api/admin/portfolio", {
          method: "POST",
          body: form,
        });
      });

      await Promise.all(uploads);
      
      setNewItem({ title: "", category: "", sortOrder: 0, showOnHome: true });
      setFiles([]);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Remove this item?")) return;
    try {
      await api(`/api/admin/portfolio?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    }
  };

  const toggleHome = async (id: string, current: boolean) => {
    try {
      await api("/api/admin/portfolio", { method: "PATCH", body: JSON.stringify({ id, showOnHome: !current }) });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  };

  const startEdit = (item: PortfolioItem) => {
    setEditingId(item.id);
    setEditForm(item);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append("id", editingId);
      form.append("title", editForm.title || "");
      form.append("category", editForm.category || "");
      form.append("sortOrder", (editForm.sortOrder || 0).toString());
      form.append("showOnHome", editForm.showOnHome ? "true" : "false");
      form.append("src", editForm.src || "");

      await api("/api/admin/portfolio", {
        method: "POST",
        body: form,
      });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-section"><div className="loading-panel">Loading portfolio...</div></div>;

  return (
    <div className="admin-section">
      <SectionLabel icon={<ImagePlus size={18} aria-hidden="true" />} label="Hero Image" />
      <form 
        className="booking-form" 
        style={{ maxWidth: "500px", marginBottom: "40px" }} 
        onSubmit={async (e) => {
          e.preventDefault();
          if (!heroFile) return;
          setHeroSaving(true);
          try {
            const form = new FormData();
            form.append("key", "hero_image");
            form.append("image", heroFile);
            await api("/api/admin/settings", { method: "POST", body: form });
            setHeroFile(null);
            alert("Hero image updated! Refresh the home page to see it.");
          } catch (err) {
            setError(err instanceof Error ? err.message : "Hero upload failed.");
          } finally {
            setHeroSaving(false);
          }
        }}
      >
        <label>
          New Hero Image
          <input type="file" accept="image/*" required onChange={e => setHeroFile(e.target.files?.[0] || null)} />
        </label>
        <button className="button button-dark submit-button" type="submit" disabled={heroSaving}>
          {heroSaving ? "Uploading..." : "Update Hero Image"}
        </button>
      </form>

      <SectionLabel icon={<ImagePlus size={18} aria-hidden="true" />} label="Portfolio Items" />
      {error && <p className="form-message is-error">{error}</p>}
      
      <div className="masonry-gallery" style={{ marginBottom: "40px" }}>
        {items.map(item => (
          <article className="gallery-item" key={item.id} style={{ minHeight: "200px" }}>
            <img src={item.src} alt={item.title} loading="lazy" />
            {editingId === item.id ? (
              <div style={{ background: "rgba(0,0,0,0.85)", padding: "15px", width: "100%", height: "100%", position: "absolute", top: 0, left: 0, zIndex: 10, display: "flex", flexDirection: "column", gap: "10px" }}>
                <input className="minimal-input" value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} placeholder="Title" style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white" }} />
                <input className="minimal-input" value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})} placeholder="Category" style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white" }} />
                <div style={{ position: "static", inset: "auto", display: "flex", gap: "10px", alignItems: "center" }}>
                  <span style={{ color: "white", fontSize: "12px" }}>Sort:</span>
                  <input className="minimal-input" type="number" value={editForm.sortOrder} onChange={e => setEditForm({...editForm, sortOrder: parseInt(e.target.value, 10)})} style={{ width: "60px", padding: "8px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white" }} />
                </div>
                <div style={{ position: "static", inset: "auto", display: "flex", gap: "10px", marginTop: "auto" }}>
                  <button className="button button-dark" style={{ flex: 1, padding: "8px", border: "1px solid rgba(255,255,255,0.3)" }} onClick={() => saveEdit()} disabled={saving}>Save</button>
                  <button className="button button-dark" style={{ flex: 1, padding: "8px", background: "transparent", border: "1px solid rgba(255,255,255,0.3)" }} onClick={() => setEditingId(null)} disabled={saving}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ background: "rgba(0,0,0,0.85)", padding: "12px", width: "100%", inset: "auto 0 0 0", position: "absolute" }}>
                <section style={{ display: "flex", flexDirection: "column", gap: "10px", position: "static", inset: "auto" }}>
                  <header style={{ position: "static", inset: "auto" }}>
                    <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.7)", display: "block", marginBottom: "4px" }}>{item.category} (Sort: {item.sortOrder})</span>
                    <h3 style={{ margin: 0, fontSize: "14px", color: "white", wordBreak: "break-word" }}>{item.title}</h3>
                  </header>
                  <aside style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", position: "static", inset: "auto", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "8px" }}>
                    <label style={{ margin: 0, padding: 0, background: "none", border: "none", color: "white", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", textTransform: "none", letterSpacing: "normal" }}>
                      <input type="checkbox" checked={item.showOnHome} onChange={() => toggleHome(item.id, item.showOnHome)} style={{ margin: 0, width: "14px", height: "14px" }} />
                      Home
                    </label>
                    <div style={{ display: "flex", gap: "6px", position: "static", inset: "auto" }}>
                      <button className="icon-button" type="button" style={{ width: "28px", height: "28px", flex: "0 0 28px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }} onClick={() => startEdit(item)} aria-label="Edit">
                        <Pencil size={12} color="#fff" aria-hidden="true" />
                      </button>
                      <button className="icon-button" type="button" style={{ width: "28px", height: "28px", flex: "0 0 28px", background: "rgba(255,0,0,0.1)", border: "1px solid rgba(255,0,0,0.3)" }} onClick={() => remove(item.id)} aria-label="Delete">
                        <Trash2 size={12} color="#ff4444" aria-hidden="true" />
                      </button>
                    </div>
                  </aside>
                </section>
              </div>
            )}
          </article>
        ))}
      </div>

      <SectionLabel icon={<Plus size={18} aria-hidden="true" />} label="Add Portfolio Item(s)" />
      <form className="booking-form" onSubmit={add} style={{ maxWidth: "500px" }}>
        <label>
          Title (Optional - uses filename if blank)
          <input value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} />
        </label>
        <label>
          Category
          <input required value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} />
        </label>
        <label>
          Sort Order
          <input type="number" required value={newItem.sortOrder} onChange={e => setNewItem({...newItem, sortOrder: parseInt(e.target.value, 10)})} />
        </label>
        <label>
          Image File(s)
          <input type="file" accept="image/*" multiple required onChange={e => setFiles(Array.from(e.target.files || []))} />
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: "8px", marginTop: "8px", marginBottom: "16px" }}>
          <input type="checkbox" checked={newItem.showOnHome} onChange={e => setNewItem({...newItem, showOnHome: e.target.checked})} style={{ width: "auto" }} />
          Show on Home Page
        </label>
        <button className="button button-dark submit-button" type="submit" disabled={saving}>
          {saving ? "Uploading..." : files.length > 1 ? `Upload ${files.length} Items` : "Add Item"}
        </button>
      </form>
    </div>
  );
}
